import { DFPSlotsProvider } from '@dsch/react-dfp';

import type {
  DataTargetingProps,
  ISeoLinks,
  DashLink,
  LifestyleLinks,
} from '../../types';

import type { AdCount } from '../../api/types/adviewApiTypes';

import { useLastSearch } from '../../components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';
import {
  HomePageData,
  useHomePage,
} from '../../components/HomePage/hooks/useHomePage';
import * as Styled from '../../components/HomePage/styles/HomePage.styled';
import { Hero } from '../../features/landing/Hero/Hero';
import { TopAdUnit } from '../../features/landing/TopAdUnit/TopAdUnit';
import { Sections } from '../../features/landing/Sections/Sections';
import { FeaturedAds } from '../../features/landing/FeaturedAds/FeaturedAds';
import { RecentAds } from '../../features/landing/RecentAds/RecentAds';
import { GoFurther } from '../../features/landing/GoFurther/GoFurther';
import { PopularSearches } from '../../features/landing/PopularSearches/PopularSearches';
import { HomePageValuationLookup } from '../../features/landing/HomePageValuationLookup/HomePageValuationLookup';
import { ValuationModal } from '../../components/HomePage/components/ValuationModal/ValuationModal';
import { useHomePageContext } from '../../contexts/HomePageContext';
import type { ISearchPageFilter } from '../../api/types/searchPageApiTypes';
import { LifeStyleCarousel } from '../../components/HomePage/components/MotorSearch/LifeStyleCarousel/LifeStyleCarousel';
import { LAST_SEARCH_DATA_TRACKING_VALUE } from '../../components/HomePage/helpers/constants';
import React from 'react';
import { VERTICAL } from '../../helpers/verticals';
import { useVerticalContext } from '../../contexts/VerticalContext';

type InitHomePage = {
  homePageData: HomePageData;
  vertical: VERTICAL;
};

export interface HomePageProps {
  cdnUrl: string;
  init: InitHomePage;
  dataTargetingProps: DataTargetingProps;
  seoLinks: ISeoLinks;
  domain: string;
  dfpNetworkId: string;
  dashLinks: DashLink[];
  lifestyleLinks: LifestyleLinks[];
  unreadInAppMessage?: string;
  adCount: AdCount;
  motorSearchFilters: ISearchPageFilter[];
}

function HomePage(props: HomePageProps) {
  const {
    cdnUrl,
    init,
    seoLinks,
    domain,
    dfpNetworkId,
    dashLinks,
    lifestyleLinks,
    adCount,
    dataTargetingProps,
    motorSearchFilters,
  } = props;
  const { homePageData, status } = useHomePage({
    init,
    dataTargetingProps,
  });
  const { vertical } = useVerticalContext();

  const { lastSearchData } = useLastSearch();

  const { isValuationModalOpen } = useHomePageContext();

  const lifestyleLinksValues = homePageData?.lifestyleLinks || lifestyleLinks;

  return (
    <Styled.HomePageWrapper>
      {/* Hero Section */}
      <Hero
        cdnUrl={cdnUrl}
        domain={domain}
        adCount={adCount}
        motorSearchFilters={motorSearchFilters}
      />
      <DFPSlotsProvider dfpNetworkId={dfpNetworkId}>
        <Styled.FlexReordering>
          {/* Top Ad Unit */}
          <TopAdUnit />
          <Styled.WhiteSection>
            <Styled.SectionBrowserHeight
              focus={vertical}
              hasSections={Boolean(homePageData?.sections.length)}
            >
              <div className="row small-collapse medium-uncollapse set-width-100">
                <div className="columns small-12" data-testid="h1-homepage">
                  <Styled.Container>
                    <Styled.StyledLastSearchCard
                      lastSearchData={lastSearchData}
                      lastSearchDataTrackingValue={
                        LAST_SEARCH_DATA_TRACKING_VALUE
                      }
                    />

                    {/* Life Style Carousel */}
                    {vertical === VERTICAL.MOTOR &&
                      lifestyleLinksValues &&
                      lifestyleLinksValues.length > 0 && (
                        <LifeStyleCarousel
                          lifestyleLinks={lifestyleLinksValues}
                        />
                      )}

                    {/* Sections */}
                    <Sections
                      status={status}
                      title={homePageData?.sectionsTitle ?? ''}
                      sections={homePageData?.sections ?? []}
                      dashLinks={dashLinks}
                    />
                  </Styled.Container>
                </div>
              </div>
            </Styled.SectionBrowserHeight>

            <HomePageValuationLookup />
          </Styled.WhiteSection>
        </Styled.FlexReordering>
        {/* Featured Ads */}
        {homePageData?.featuredAds && (
          <FeaturedAds
            cdnUrl={cdnUrl}
            domain={domain}
            featuredAds={homePageData.featuredAds}
          />
        )}
        {/* Recent Ads */}
        {(homePageData?.recentAds?.length ?? 0) > 0 && (
          <RecentAds
            domain={domain}
            recentAds={homePageData?.recentAds ?? []}
            recentAdsUrl={homePageData?.recentAdsUrl ?? null}
          />
        )}
        {/* Go Further */}
        <GoFurther dashLinks={dashLinks} />
      </DFPSlotsProvider>
      {/* Popular Searches */}
      <PopularSearches seoLinks={seoLinks} />
      {isValuationModalOpen && <ValuationModal />}
    </Styled.HomePageWrapper>
  );
}

export { HomePage };
